// File: frontend/src/pages/admissions/AdmissionList.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import PageHeader from '../../components/PageHeader';

const AdmissionList = () => {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 1,
    currentPage: 1,
  });

  // Modal view state for selected admission
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  // Function to fetch paginated admissions list from API
  const fetchAdmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '10',
      });

      if (statusFilter !== 'all') {
        queryParams.append('status', statusFilter);
      }
      if (search.trim()) {
        queryParams.append('search', search.trim());
      }

      const response = await api.get(`/api/v1/admissions?${queryParams.toString()}`);
      const data = response.data?.data;

      if (data) {
        setAdmissions(data.admissions || []);
        setPagination(data.pagination || { totalRecords: 0, totalPages: 1, currentPage: 1 });
      }
    } catch (err) {
      console.error('Error loading admissions list:', err);
      setError(err.response?.data?.message || 'Failed to load admissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  // Load admissions on component mount and dependencies change
  useEffect(() => {
    fetchAdmissions();
  }, [fetchAdmissions]);

  // Function to update status of an admission
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      setError('');
      await api.put(`/api/v1/admissions/${id}/status`, { status: newStatus });
      await fetchAdmissions();
      if (selectedAdmission && selectedAdmission.id === id) {
        setSelectedAdmission((prev) => (prev ? { ...prev, status: newStatus } : null));
      }
    } catch (err) {
      console.error(`Error updating status to ${newStatus}:`, err);
      setError(err.response?.data?.message || `Failed to update status to ${newStatus}.`);
    }
  };

  // Helper function to render color-coded badge based on status
  const getStatusBadge = (status) => {
    try {
      const normalized = (status || 'pending').toLowerCase();
      switch (normalized) {
        case 'pending':
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
              Pending
            </span>
          );
        case 'reviewing':
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
              Reviewing
            </span>
          );
        case 'approved':
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
              Approved
            </span>
          );
        case 'rejected':
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
              Rejected
            </span>
          );
        case 'enrolled':
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
              Enrolled
            </span>
          );
        default:
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
              {status}
            </span>
          );
      }
    } catch (err) {
      console.error('Error rendering status badge:', err);
      return <span className="text-xs text-slate-500">{status}</span>;
    }
  };

  // Function to handle viewing admission detail modal
  const handleViewDetail = async (admission) => {
    try {
      setSelectedAdmission(admission);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error opening view detail modal:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Page Navigation Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 w-full">
        <PageHeader
          title="Admissions Management"
          subtitle="Review and manage new student enrollment applications"
          breadcrumbs={[{ label: 'Admissions' }]}
        >
          <button
            onClick={() => navigate('/admissions/new')}
            className="inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-blue-600 text-white font-medium px-4 py-2 rounded-xl transition duration-200 shadow-sm text-xs shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            <span>New Admission</span>
          </button>
        </PageHeader>
      </div>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-6 space-y-6">

        {/* Error Alert */}
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="font-bold ml-2">×</button>
          </div>
        )}

        {/* Filters and Search Bar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row gap-4 justify-between items-center">
          {/* Search Bar */}
          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search student or father name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm placeholder-slate-400"
            />
            <svg
              className="w-5 h-5 text-slate-400 absolute left-3 top-2.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-slate-600 shrink-0">Filter Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full sm:w-48 px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-slate-800 bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="reviewing">Reviewing</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="enrolled">Enrolled</option>
            </select>
          </div>
        </div>

        {/* Admissions Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-slate-500 flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">Loading admission applications...</span>
            </div>
          ) : admissions.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <p className="text-base font-semibold text-slate-700">No admission applications found.</p>
              <p className="text-xs text-slate-400 mt-1">Try adjusting search or status filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <th className="py-3.5 px-4">Admission No</th>
                    <th className="py-3.5 px-4">Student Name</th>
                    <th className="py-3.5 px-4">Father Name</th>
                    <th className="py-3.5 px-4">Class</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4">Date</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {admissions.map((adm) => (
                    <tr key={adm.id} className="hover:bg-slate-50/80 transition">
                      <td className="py-3.5 px-4 font-mono font-medium text-slate-900">
                        {adm.admission_no}
                      </td>
                      <td className="py-3.5 px-4 font-semibold text-slate-800">
                        {adm.student_name}
                      </td>
                      <td className="py-3.5 px-4">{adm.father_name}</td>
                      <td className="py-3.5 px-4 font-medium text-slate-600">
                        {adm.class_applying}
                      </td>
                      <td className="py-3.5 px-4">{getStatusBadge(adm.status)}</td>
                      <td className="py-3.5 px-4 text-xs text-slate-500">
                        {adm.created_at ? new Date(adm.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="py-3.5 px-4 text-right space-x-2">
                        <button
                          onClick={() => handleViewDetail(adm)}
                          className="px-2.5 py-1 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition"
                        >
                          View
                        </button>
                        {adm.status !== 'approved' && adm.status !== 'enrolled' && (
                          <button
                            onClick={() => handleUpdateStatus(adm.id, 'approved')}
                            className="px-2.5 py-1 text-xs font-medium bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition"
                          >
                            Approve
                          </button>
                        )}
                        {adm.status !== 'rejected' && (
                          <button
                            onClick={() => handleUpdateStatus(adm.id, 'rejected')}
                            className="px-2.5 py-1 text-xs font-medium bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg transition"
                          >
                            Reject
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Footer */}
          {!loading && pagination.totalPages > 1 && (
            <div className="px-6 py-4 bg-white border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-500">
                Page <span className="font-semibold text-slate-800">{pagination.currentPage}</span> of{' '}
                <span className="font-semibold text-slate-800">{pagination.totalPages}</span> ({pagination.totalRecords} records)
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                  className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </main>

      {/* Admission Detail View Modal */}
      {isModalOpen && selectedAdmission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 border border-slate-200 shadow-2xl space-y-4">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  Admission Details ({selectedAdmission.admission_no})
                </h3>
                <p className="text-xs text-slate-500">Applied for Class: {selectedAdmission.class_applying}</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold"
              >
                ×
              </button>
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-400 text-xs block">Student Name:</span> <strong>{selectedAdmission.student_name}</strong></div>
                <div><span className="text-slate-400 text-xs block">Father Name:</span> <strong>{selectedAdmission.father_name}</strong></div>
                <div><span className="text-slate-400 text-xs block">Phone:</span> <strong>{selectedAdmission.phone}</strong></div>
                <div><span className="text-slate-400 text-xs block">Email:</span> <strong>{selectedAdmission.email || 'N/A'}</strong></div>
                <div><span className="text-slate-400 text-xs block">Gender:</span> <strong>{selectedAdmission.gender || 'N/A'}</strong></div>
                <div><span className="text-slate-400 text-xs block">Status:</span> {getStatusBadge(selectedAdmission.status)}</div>
              </div>
              {selectedAdmission.address && (
                <div>
                  <span className="text-slate-400 text-xs block">Address:</span>
                  <p className="text-xs bg-slate-50 p-2.5 rounded-lg text-slate-700 mt-1">{selectedAdmission.address}</p>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-xl transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmissionList;
